import { z } from 'zod';
import { specRowSchema } from './catalog.js';
import { flyerIconSchema } from './product-texts.js';
import { storeDesignSchema, storeThemeSchema } from './storefront.js';

/**
 * A product's marketing flyer (docs/FLYERS.md, adr/0053): a square image a store shares on social
 * networks and WhatsApp. Every store template has a flyer of its own; a store's flyer is its
 * template's, in the store's colours and logo, with the store's own texts of the product. Nothing
 * is written for a flyer: the admin draws it in the browser from what the store's product page
 * shows, so a new template, colour, text or price changes every flyer the next time it is drawn.
 */

/**
 * How the product's photo is changed on this store's flyer of this product (adr/0032), so the
 * flyer is not the catalog's photo again: it is always mirrored, and turned, enlarged (its edges
 * cut) and recoloured a little, by amounts of this store and product.
 */
export const flyerDesignSchema = z.object({
  photo: z.object({
    /** Degrees, either way. */
    rotate: z.number().min(-6).max(6),
    /** Over the size that fits its box: the edges of the photo are cut. */
    zoom: z.number().min(1).max(1.3),
    /** Degrees around the colour wheel. */
    hue: z.number().min(-30).max(30),
    saturate: z.number().min(0.7).max(1.3),
  }),
});
export type FlyerDesign = z.infer<typeof flyerDesignSchema>;

/** The most spec rows and strengths a flyer is given: no template shows more. */
export const FLYER_MAX_SPECS = 8;
export const FLYER_MAX_BENEFITS = 5;

/** Everything the admin needs to draw one product's flyer. */
export const productFlyerSchema = z.object({
  productId: z.uuid(),
  /** The product's title in the store (its name when it has none). */
  title: z.string(),
  /** The first paragraph of the store's description, as plain text; empty without one. */
  description: z.string(),
  /** The store's SKU of the product. */
  sku: z.string(),
  /** The name of the product's category in the store; null when it has none. */
  category: z.string().nullable(),
  /**
   * What the store's product page shows of the maker (adr/0030, adr/0041): the manufacturer, the
   * model and the country of origin only when the page shows them, and the manufacturer logo the
   * product carries (a file name under the store's media route).
   */
  maker: z.object({
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
    country: z.string().nullable(),
    logo: z.string().nullable(),
  }),
  /** The product's spec rows as shoppers read them, without the rows of the maker above. */
  specs: z.array(specRowSchema).max(FLYER_MAX_SPECS),
  /** The product's strengths written with its copy (adr/0048): a title and an icon each. */
  benefits: z.array(z.object({ icon: flyerIconSchema, title: z.string() })).max(FLYER_MAX_BENEFITS),
  /** The first image's file name under the store's media route; null without one. */
  image: z.string().nullable(),
  /** Null when the store does not show prices, or the product has none (a price of 0). */
  price: z
    .object({
      amountMinor: z.number().int().min(0),
      compareAtMinor: z.number().int().min(0).nullable(),
    })
    .nullable(),
  store: z.object({
    name: z.string(),
    phone: z.string().nullable(),
    whatsapp: z.string().nullable(),
    website: z.string().nullable(),
    /** The logo's file name under the store's media route; null without one. */
    logo: z.string().nullable(),
  }),
  theme: storeThemeSchema,
  /** The store's template (it chooses the flyer), corners, type and dark colour. */
  storeDesign: storeDesignSchema,
  design: flyerDesignSchema,
});
export type ProductFlyer = z.infer<typeof productFlyerSchema>;
